<template>
  <div class="container-fluid mt-5">
    <card v-if="showApplyCard" class="mb-4">
      <template slot="header">
        <div class="row align-items-center">
          <div class="col">
            <h5 class="mb-0">Aplicar fecha fin de vida útil en activos</h5>
            <p class="text-muted mb-0 mt-1 small">
              Calcula y asigna <strong>fecha_fin_vida_util</strong> (fecha fabricación/adquisición + años del modelo).
              Solo sin fecha, o recalcular para corregir fechas ya guardadas.
              <strong>Marcar fin vida útil</strong> detecta activos con fecha ≤ hoy y les pone <code>fin_vida_util_alcanzada</code>.
            </p>
          </div>
          <div class="col-auto">
            <button
              type="button"
              class="btn btn-primary mr-2"
              :disabled="applyingFechas"
              @click="aplicarFechasFinVidaUtil(false)"
            >
              {{ applyingFechas && !applyingRecalcular ? "Aplicando…" : "Solo activos sin fecha" }}
            </button>
            <button
              type="button"
              class="btn btn-outline-primary mr-2"
              :disabled="applyingFechas"
              @click="aplicarFechasFinVidaUtil(true)"
            >
              {{ applyingFechas && applyingRecalcular ? "Recalculando…" : "Recalcular todas las fechas" }}
            </button>
            <button
              type="button"
              class="btn btn-warning"
              :disabled="applyingMarcarFinVidaUtil"
              @click="marcarFinVidaUtil()"
            >
              {{ applyingMarcarFinVidaUtil ? "Marcando…" : "Marcar fin vida útil" }}
            </button>
          </div>
        </div>
      </template>
      <div v-if="applyProgress.visible" class="card-body pt-0">
        <div class="alert alert-secondary mb-0">
          <strong>Progreso:</strong> {{ applyProgress.text }}
        </div>
      </div>
      <div v-else-if="applyResult" class="card-body pt-0">
        <div class="alert alert-info mb-0">
          <strong>Resultado:</strong>
          <template v-if="applyResult.total_candidatos != null">
            {{ applyResult.total_candidatos }} candidatos →
          </template>
          <template v-else>
            {{ applyResult.total_sin_fecha }} activos sin fecha →
          </template>
          <strong>{{ applyResult.actualizados }} actualizados</strong>,
          {{ applyResult.sin_calcular }} sin calcular,
          {{ applyResult.errores }} errores.
        </div>
      </div>
      <div v-else-if="marcarFinVidaUtilResult" class="card-body pt-0">
        <div class="alert alert-warning mb-0">
          <strong>Marcar fin vida útil:</strong>
          {{ marcarFinVidaUtilResult.total_evaluados ?? marcarFinVidaUtilResult.total_detectados }} evaluados →
          <strong>{{ marcarFinVidaUtilResult.marcados }} sincronizados</strong>
          (<code>true</code>: {{ marcarFinVidaUtilResult.marcados_true ?? marcarFinVidaUtilResult.marcados }},
          <code>false</code>: {{ marcarFinVidaUtilResult.marcados_false ?? 0 }}),
          {{ marcarFinVidaUtilResult.errores }} errores.
        </div>
      </div>
    </card>

    <card>
      <template slot="header">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="mb-0">Vida útil de modelos</h3>
            <p class="text-muted mb-0 mt-1">
              Revisar y editar la vida útil de todos los modelos del sistema.
            </p>
          </div>
          <div class="col-auto">
            <button
              type="button"
              class="btn btn-warning btn-sm"
              :disabled="applyingMarcarFinVidaUtil"
              @click="marcarFinVidaUtil()"
            >
              {{ applyingMarcarFinVidaUtil ? "Marcando…" : "Actualizar fin_vida_util_alcanzada" }}
            </button>
          </div>
        </div>
      </template>
      <div class="card-body">
        <div v-if="marcarFinVidaUtilResult" class="alert alert-warning">
          <strong>Actualizar fin_vida_util_alcanzada:</strong>
          {{ marcarFinVidaUtilResult.total_evaluados ?? marcarFinVidaUtilResult.total_detectados }} evaluados →
          <strong>{{ marcarFinVidaUtilResult.marcados }} sincronizados</strong>
          (<code>true</code>: {{ marcarFinVidaUtilResult.marcados_true ?? marcarFinVidaUtilResult.marcados }},
          <code>false</code>: {{ marcarFinVidaUtilResult.marcados_false ?? 0 }}),
          {{ marcarFinVidaUtilResult.errores }} errores.
        </div>
        <div v-if="loading" class="text-center py-5">
          <img src="/img/loading.gif" style="height: 80px; width: 80px" alt="Cargando" />
          <p class="mt-2">Cargando modelos...</p>
        </div>
        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>
        <div v-else-if="grupos.length === 0" class="alert alert-info">
          No hay modelos.
        </div>
        <div v-else>
          <section
            v-for="(grupo, idx) in grupos"
            :key="idx"
            class="mb-4"
          >
            <h6 class="text-primary mb-2">{{ grupo.subtipoName }}</h6>
            <div class="table-wrap">
              <table class="table table-bordered table-sm">
                <thead class="thead-light">
                  <tr>
                    <th>Modelo</th>
                    <th style="width: 140px">Vida útil</th>
                    <th style="width: 260px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in grupo.modelos" :key="row.id">
                    <td>{{ row.nombre }}</td>
                    <td>
                      <input
                        type="number"
                        :value="editValues[row.id]"
                        min="1"
                        max="99"
                        class="form-control form-control-sm text-center"
                        style="max-width: 80px"
                        @input="setVidaUtil(row.id, $event.target.value)"
                      />
                    </td>
                    <td>
                      <button
                        v-if="dirtyIds[row.id]"
                        type="button"
                        class="btn btn-primary btn-sm"
                        :disabled="savingIds[row.id] || applyingModeloIds[row.id]"
                        @click="guardarModelo(row)"
                      >
                        {{ savingIds[row.id] ? "Guardando…" : "Guardar" }}
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-primary btn-sm ml-2"
                        :disabled="savingIds[row.id] || applyingModeloIds[row.id]"
                        @click="aplicarModeloEnActivos(row)"
                      >
                        {{ applyingModeloIds[row.id] ? "Aplicando…" : "Aplicar en activos" }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import axios from "axios";

const url = process.env.VUE_APP_API_BASE_URL;

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default {
  layout: "DashboardLayout",
  data() {
    return {
      showApplyCard: false,
      loading: true,
      error: null,
      grupos: [],
      editValues: {},
      dirtyIds: {},
      savingIds: {},
      applyingModeloIds: {},
      applyingFechas: false,
      applyingRecalcular: false,
      applyResult: null,
      applyProgress: { visible: false, text: "" },
      applyingMarcarFinVidaUtil: false,
      marcarFinVidaUtilResult: null,
    };
  },
  created() {
    this.loadModelos();
  },
  methods: {
    async fetchAllModelos(baseParams) {
      const all = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = { ...baseParams, "pagination[page]": page };
        const { data } = await axios.get(`${url}/modelos`, {
          params,
          headers: { ...getAuthHeaders(), Accept: "application/json" },
        });
        const list = Array.isArray(data) ? data : data.data || [];
        all.push(...list);
        const total = data.meta?.pagination?.total ?? list.length;
        const pageCount = data.meta?.pagination?.pageCount ?? 1;
        hasMore = page < pageCount && list.length > 0;
        page += 1;
      }
      return all;
    },
    async loadModelos() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          "populate[0]": "division",
          "populate[1]": "tipo",
          "populate[2]": "subtipo",
          "pagination[pageSize]": 500,
        };
        const list = await this.fetchAllModelos(params);
        const resolveRel = (r) => {
          if (!r) return null;
          if (r.data && r.data.attributes) return { id: r.data.id, ...r.data.attributes };
          if (r.id && r.nombre) return r;
          if (r.attributes) return { id: r.id, ...r.attributes };
          return null;
        };
        const modelos = list.map((item) => {
          const attrs = item.attributes || item;
          const v = Number(attrs.vida_util);
          const vida_util = !Number.isNaN(v) && v >= 1 ? v : 10;
          return {
            id: item.id,
            nombre: attrs.nombre,
            vida_util,
            subtipo: resolveRel(attrs.subtipo),
          };
        });

        const editValues = {};
        const bySubtipo = {};
        for (const m of modelos) {
          const key = m.subtipo?.id ?? "sin-subtipo";
          const name = m.subtipo?.nombre ?? "Sin subtipo";
          if (!bySubtipo[key]) bySubtipo[key] = { subtipoName: name, modelos: [] };
          bySubtipo[key].modelos.push({
            id: m.id,
            nombre: m.nombre,
            vida_util: m.vida_util,
          });
          editValues[m.id] = m.vida_util;
        }

        this.editValues = editValues;
        this.dirtyIds = {};
        this.savingIds = {};
        this.applyingModeloIds = {};
        this.grupos = Object.values(bySubtipo);
      } catch (err) {
        console.error(err);
        this.error =
          err.response?.data?.error?.message ||
          err.message ||
          "Error al cargar los modelos.";
      } finally {
        this.loading = false;
      }
    },
    setVidaUtil(id, value) {
      const n = parseInt(value, 10);
      const val = Number.isNaN(n) ? 10 : Math.max(1, Math.min(99, n));
      this.$set(this.editValues, id, val);
      this.$set(this.dirtyIds, id, true);
    },
    async guardarModelo(row, options = {}) {
      const silent = options.silent === true;
      this.$set(this.savingIds, row.id, true);
      const valor = this.editValues[row.id];
      try {
        await axios.put(
          `${url}/modelos/${row.id}`,
          { data: { vida_util: valor } },
          { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
        );
        this.$set(this.dirtyIds, row.id, false);
        if (!silent) {
          this.$notify({
            type: "success",
            message: `Vida útil de "${row.nombre}" actualizada a ${valor}.`,
          });
        }
        return true;
      } catch (err) {
        if (!silent) {
          this.$notify({
            type: "danger",
            message:
              err.response?.data?.error?.message || "Error al guardar.",
          });
        }
        return false;
      } finally {
        this.$set(this.savingIds, row.id, false);
      }
    },
    async aplicarModeloEnActivos(row) {
      this.$set(this.applyingModeloIds, row.id, true);
      const BATCH_SIZE = 300;
      const REQUEST_TIMEOUT = 120000;
      try {
        // Si cambió la vida útil, guardar primero para calcular con el valor actualizado
        if (this.dirtyIds[row.id]) {
          const ok = await this.guardarModelo(row, { silent: true });
          if (!ok) {
            this.$notify({
              type: "danger",
              message: `No se pudo guardar la vida útil de "${row.nombre}".`,
            });
            return;
          }
        }

        let offset = 0;
        let totalActualizados = 0;
        let totalSinCalcular = 0;
        let totalErrores = 0;
        let hasMore = true;

        while (hasMore) {
          const params =
            `?recalcular=true&includeRetired=true&modeloId=${row.id}` +
            `&batchLimit=${BATCH_SIZE}&batchOffset=${offset}`;
          const { data } = await axios.post(
            `${url}/activos-vida-util/aplicar-sugeridas${params}`,
            {},
            {
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              timeout: REQUEST_TIMEOUT,
            }
          );
          totalActualizados += data.actualizados || 0;
          totalSinCalcular += data.sin_calcular || 0;
          totalErrores += data.errores || 0;
          hasMore = data.hasMore === true;
          offset += BATCH_SIZE;
        }

        this.$notify({
          type: "success",
          message:
            `Modelo "${row.nombre}": ${totalActualizados} activos actualizados, ` +
            `${totalSinCalcular} sin calcular, ${totalErrores} errores.`,
        });
      } catch (err) {
        this.$notify({
          type: "danger",
          message:
            err.response?.data?.error?.message ||
            err.message ||
            `Error al aplicar cálculo en activos de "${row.nombre}".`,
        });
      } finally {
        this.$set(this.applyingModeloIds, row.id, false);
      }
    },
    async aplicarFechasFinVidaUtil(recalcular = false) {
      this.applyingFechas = true;
      this.applyingRecalcular = recalcular;
      this.applyResult = null;
      this.applyProgress = { visible: false, text: "" };
      const BATCH_SIZE = 300;
      const REQUEST_TIMEOUT = 120000;

      try {
        if (recalcular) {
          let offset = 0;
          let totalActualizados = 0;
          let totalSinCalcular = 0;
          let totalErrores = 0;
          let hasMore = true;
          let batchNum = 0;

          while (hasMore) {
            batchNum += 1;
            this.applyProgress = {
              visible: true,
              text: `Recalculando lote ${batchNum}… (${totalActualizados} actualizados hasta ahora)`,
            };
            const params = `?recalcular=true&batchLimit=${BATCH_SIZE}&batchOffset=${offset}`;
            const { data } = await axios.post(
              `${url}/activos-vida-util/aplicar-sugeridas${params}`,
              {},
              {
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                timeout: REQUEST_TIMEOUT,
              }
            );
            totalActualizados += data.actualizados || 0;
            totalSinCalcular += data.sin_calcular || 0;
            totalErrores += data.errores || 0;
            hasMore = data.hasMore === true;
            offset += BATCH_SIZE;
          }

          this.applyProgress = { visible: false, text: "" };
          this.applyResult = {
            actualizados: totalActualizados,
            sin_calcular: totalSinCalcular,
            errores: totalErrores,
            total_candidatos: totalActualizados + totalSinCalcular + totalErrores,
          };
          this.$notify({
            type: "success",
            message: `Recalculado: ${totalActualizados} activos actualizados.`,
          });
        } else {
          const { data } = await axios.post(
            `${url}/activos-vida-util/aplicar-sugeridas`,
            {},
            {
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              timeout: REQUEST_TIMEOUT,
            }
          );
          this.applyResult = data;
          this.$notify({
            type: "success",
            message: `Aplicado: ${data.actualizados} activos actualizados.`,
          });
        }
      } catch (err) {
        this.applyProgress = { visible: false, text: "" };
        this.$notify({
          type: "danger",
          message: err.response?.data?.error?.message || err.message || "Error al aplicar.",
        });
      } finally {
        this.applyingFechas = false;
        this.applyingRecalcular = false;
      }
    },
    async marcarFinVidaUtil(marcarRetirado = false) {
      this.applyingMarcarFinVidaUtil = true;
      this.marcarFinVidaUtilResult = null;
      this.applyResult = null;
      try {
        const params = new URLSearchParams();
        params.append("includeRetired", "true");
        if (marcarRetirado) params.append("marcarRetirado", "true");
        const { data } = await axios.post(
          `${url}/activos-vida-util/marcar-fin-vida-util?${params}`,
          {},
          {
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
            timeout: 60000,
          }
        );
        this.marcarFinVidaUtilResult = data;
        this.$notify({
          type: "success",
          message:
            `Fin vida útil sincronizada: ${data.marcados} activos ` +
            `(true: ${data.marcados_true ?? data.marcados}, false: ${data.marcados_false ?? 0}).`,
        });
      } catch (err) {
        this.$notify({
          type: "danger",
          message:
            err.response?.data?.error?.message || err.message || "Error al marcar fin vida útil.",
        });
      } finally {
        this.applyingMarcarFinVidaUtil = false;
      }
    },
  },
};
</script>

<style scoped>
.table-wrap {
  background-color: #fafbfc;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}
.table-wrap .table {
  margin-bottom: 0;
  background: #fff;
}
.table th,
.table td {
  color: #303133;
  vertical-align: middle;
}
</style>
